/* eslint-disable react-hooks/refs -- @dnd-kit node refs are used during render by design */
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { WeeklyPlanDay } from '@gb-ticket/shared-types';
import { ChevronDownIcon, ChevronUpIcon, PencilIcon, Trash2Icon, UserPlusIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

import {
  DAY_LABEL,
  DAY_SHORT,
  LIST_ROW_CLASS,
  assignmentField,
  cellField,
  dayHeaderClass,
  lastNameOf,
  personChrome,
  rowTitle,
  type DraftGroup,
  type DraftRow,
} from '@/products/intranet/pages/weekly-plan-fields';

const COL_NR = '9rem';
const COL_LAST = '12rem';
const COL_FIRST = '9rem';
const COL_ROLE = '10rem';

const COL_STYLE = {
  nr: { width: COL_NR, minWidth: COL_NR, maxWidth: COL_NR, left: 0 },
  last: {
    width: COL_LAST,
    minWidth: COL_LAST,
    maxWidth: COL_LAST,
    left: COL_NR,
  },
  first: {
    width: COL_FIRST,
    minWidth: COL_FIRST,
    maxWidth: COL_FIRST,
    left: `calc(${COL_NR} + ${COL_LAST})`,
  },
  role: {
    width: COL_ROLE,
    minWidth: COL_ROLE,
    maxWidth: COL_ROLE,
    left: `calc(${COL_NR} + ${COL_LAST} + ${COL_FIRST})`,
  },
} as const;

type Colleague = { id: string; displayName: string };

type Section =
  { kind: 'none'; rows: DraftRow[] } | { kind: 'group'; group: DraftGroup; rows: DraftRow[] };

export function WeeklyPlanGroupList({
  rows,
  groups,
  columns,
  marked,
  focusedDay,
  onFocusDay,
  writable,
  meId,
  selectedIds,
  recent,
  patchRow,
  removeRow,
  moveRow,
  moveGroup,
  renameGroup,
  deleteGroup,
  assignGroup,
  dropOnRow,
  reorderGroup,
  addMembers,
  colleagues,
  colleaguesPending,
  memberQuery,
  onMemberQuery,
  onMembersOpen,
  planUserIds,
}: {
  rows: DraftRow[];
  groups: DraftGroup[];
  columns: WeeklyPlanDay[];
  marked: WeeklyPlanDay | null;
  focusedDay: WeeklyPlanDay;
  onFocusDay: (day: WeeklyPlanDay) => void;
  writable: boolean;
  meId: string | undefined;
  selectedIds: readonly string[];
  recent: (row: DraftRow, day: WeeklyPlanDay) => boolean;
  patchRow: (key: string, recipe: (row: DraftRow) => DraftRow) => void;
  removeRow: (key: string) => void;
  moveRow: (key: string, direction: -1 | 1) => void;
  moveGroup: (id: string, direction: -1 | 1) => void;
  renameGroup: (id: string, name: string) => void;
  deleteGroup: (id: string) => void;
  assignGroup: (key: string, groupId: string | null) => void;
  dropOnRow: (key: string, targetKey: string) => void;
  reorderGroup: (id: string, overId: string) => void;
  addMembers: (groupId: string, people: Colleague[]) => void;
  colleagues: Colleague[];
  colleaguesPending: boolean;
  memberQuery: string;
  onMemberQuery: (value: string) => void;
  onMembersOpen: (open: boolean) => void;
  planUserIds: ReadonlySet<string>;
}) {
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameText, setRenameText] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [memberGroupId, setMemberGroupId] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const span = columns.length + 4;
  const orderedGroups = groups.slice().sort((left, right) => left.sortOrder - right.sortOrder);
  const sections: Section[] = [];
  const loose = rows.filter((row) => row.groupId === null);
  if (loose.length > 0) {
    sections.push({ kind: 'none', rows: loose });
  }
  for (const group of orderedGroups) {
    sections.push({
      kind: 'group',
      group,
      rows: rows.filter((row) => row.groupId === group.id),
    });
  }
  const sortableIds = [
    ...orderedGroups.map((group) => `group:${group.id}`),
    ...rows.map((row) => `row:${row.key}`),
  ];
  const renameGroupRow = orderedGroups.find((group) => group.id === renameId) ?? null;
  const deleteGroupRow = orderedGroups.find((group) => group.id === deleteId) ?? null;
  const memberGroup = orderedGroups.find((group) => group.id === memberGroupId) ?? null;
  const query = memberQuery.trim();
  const queryReady = query.length >= 2;
  const hits = queryReady
    ? colleagues.filter((person) => person.displayName.toLowerCase().includes(query.toLowerCase()))
    : [];
  const memberRows = memberGroup ? rows.filter((row) => row.groupId === memberGroup.id) : [];

  function onDragEnd(event: DragEndEvent) {
    const overId = event.over ? String(event.over.id) : '';
    const activeId = String(event.active.id);
    if (!overId || overId === activeId) {
      return;
    }
    if (activeId.startsWith('group:')) {
      if (!overId.startsWith('group:')) {
        return;
      }
      reorderGroup(activeId.slice('group:'.length), overId.slice('group:'.length));
      return;
    }
    if (!activeId.startsWith('row:')) {
      return;
    }
    const key = activeId.slice('row:'.length);
    if (overId === 'bucket:none') {
      assignGroup(key, null);
      return;
    }
    if (overId.startsWith('group:')) {
      assignGroup(key, overId.slice('group:'.length));
      return;
    }
    if (overId.startsWith('row:')) {
      dropOnRow(key, overId.slice('row:'.length));
    }
  }

  function openMembers(groupId: string) {
    setMemberGroupId(groupId);
    setPicked([]);
    onMemberQuery('');
    onMembersOpen(true);
  }

  function closeMembers(open: boolean) {
    if (open) {
      return;
    }
    setMemberGroupId(null);
    setPicked([]);
    onMemberQuery('');
    onMembersOpen(false);
  }

  function requestDelete(group: DraftGroup, memberCount: number) {
    if (memberCount === 0) {
      deleteGroup(group.id);
      return;
    }
    setDeleteId(group.id);
  }

  const body = sections.length === 0 ? <p>Keine Einträge.</p> : null;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        <div className="grid gap-3 md:hidden" data-testid="weekly-plan-cards">
          <div role="group" aria-label="Tag" className="flex flex-wrap gap-1">
            {columns.map((day) => (
              <Button
                key={day}
                type="button"
                variant="ghost"
                aria-label={DAY_LABEL[day]}
                aria-current={marked === day ? 'date' : undefined}
                aria-pressed={focusedDay === day}
                className={cn(
                  'min-h-11',
                  marked === day && 'bg-status-info text-status-info-foreground',
                )}
                onClick={() => onFocusDay(day)}
              >
                {DAY_SHORT[day]}
              </Button>
            ))}
          </div>
          {body}
          {sections.map((section) => (
            <section
              key={section.kind === 'none' ? 'none' : section.group.id}
              className="grid gap-2"
            >
              <GroupHeading
                section={section}
                groups={orderedGroups}
                writable={writable}
                onMove={moveGroup}
                onRename={(group) => {
                  setRenameId(group.id);
                  setRenameText(group.name);
                }}
                onMembers={openMembers}
                onDelete={requestDelete}
              />
              {section.kind === 'group' && section.rows.length === 0 ? (
                <p>Keine Mitglieder.</p>
              ) : null}
              {section.rows.map((row, index) => (
                <MemberCard
                  key={row.key}
                  row={row}
                  index={index}
                  total={section.rows.length}
                  focusedDay={focusedDay}
                  writable={writable}
                  meId={meId}
                  selectedIds={selectedIds}
                  recent={recent}
                  patchRow={patchRow}
                  removeRow={removeRow}
                  moveRow={moveRow}
                />
              ))}
            </section>
          ))}
        </div>
        <div className="hidden min-w-0 md:block" data-testid="weekly-plan-grid">
          <div className="max-h-[70vh] overflow-auto">
            <Table scroll={false} aria-label="Wochenplan" className="table-fixed">
              <colgroup>
                <col style={{ width: COL_NR }} />
                <col style={{ width: COL_LAST }} />
                <col style={{ width: COL_FIRST }} />
                <col style={{ width: COL_ROLE }} />
              </colgroup>
              <TableHeader className="sticky top-0 z-20 bg-background">
                <TableRow>
                  <TableHead className="sticky z-30 bg-background" style={COL_STYLE.nr}>
                    lfd. Nr.
                  </TableHead>
                  <TableHead className="sticky z-30 bg-background" style={COL_STYLE.last}>
                    Nachname
                  </TableHead>
                  <TableHead className="sticky z-30 bg-background" style={COL_STYLE.first}>
                    Vorname
                  </TableHead>
                  <TableHead className="sticky z-30 bg-background" style={COL_STYLE.role}>
                    Einsatz als
                  </TableHead>
                  {columns.map((day) => (
                    <TableHead
                      key={day}
                      aria-label={DAY_LABEL[day]}
                      aria-current={marked === day ? 'date' : undefined}
                      className={`min-w-32 ${dayHeaderClass(marked === day)}`}
                    >
                      {DAY_LABEL[day]}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sections.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={span}>Keine Einträge.</TableCell>
                  </TableRow>
                ) : (
                  sections.map((section) => (
                    <SectionRows
                      key={section.kind === 'none' ? 'none' : section.group.id}
                      section={section}
                      groups={orderedGroups}
                      span={span}
                      columns={columns}
                      writable={writable}
                      meId={meId}
                      selectedIds={selectedIds}
                      recent={recent}
                      patchRow={patchRow}
                      removeRow={removeRow}
                      moveRow={moveRow}
                      moveGroup={moveGroup}
                      onRename={(group) => {
                        setRenameId(group.id);
                        setRenameText(group.name);
                      }}
                      onMembers={openMembers}
                      onDelete={requestDelete}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </SortableContext>
      <Dialog
        open={renameGroupRow !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRenameId(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gruppe umbenennen</DialogTitle>
          </DialogHeader>
          <Input
            aria-label="Gruppenname"
            value={renameText}
            onChange={(event) => setRenameText(event.target.value)}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRenameId(null)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              disabled={renameText.trim().length < 2}
              onClick={() => {
                if (!renameGroupRow) {
                  return;
                }
                renameGroup(renameGroupRow.id, renameText);
                setRenameId(null);
              }}
            >
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={deleteGroupRow !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteId(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gruppe löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Die Gruppe hat noch Mitglieder. Mitglieder zuerst einer anderen Gruppe zuordnen — oder
              das Löschen ausdrücklich bestätigen. Bestätigtes Löschen hebt nur die Zuordnung auf.
              Personen und Zelltexte bleiben unter „Ohne Gruppe“. Niemand wird aus dem Plan
              entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                if (deleteGroupRow) {
                  deleteGroup(deleteGroupRow.id);
                }
                setDeleteId(null);
              }}
            >
              Gruppe löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={memberGroup !== null} onOpenChange={closeMembers}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mitglieder hinzufügen</DialogTitle>
          </DialogHeader>
          {memberGroup ? <p>{memberGroup.name}</p> : null}
          <Input
            aria-label="Person in der Gruppe suchen"
            value={memberQuery}
            onChange={(event) => onMemberQuery(event.target.value)}
          />
          {queryReady ? null : <p>Mindestens zwei Buchstaben.</p>}
          {queryReady && colleaguesPending ? <p>Personen werden geladen …</p> : null}
          {queryReady && !colleaguesPending && hits.length === 0 ? <p>Keine Treffer.</p> : null}
          {queryReady && !colleaguesPending && hits.length > 0 ? (
            <ul className="grid gap-2">
              {hits.map((person) => {
                const taken = planUserIds.has(person.id);
                const checked = picked.includes(person.id);
                return (
                  <li key={person.id} className="flex items-center gap-2">
                    <Checkbox
                      checked={checked}
                      disabled={taken}
                      aria-label={person.displayName}
                      onCheckedChange={(value) => {
                        setPicked((current) =>
                          value === true
                            ? [...current, person.id]
                            : current.filter((id) => id !== person.id),
                        );
                      }}
                    />
                    <span>{person.displayName}</span>
                    {taken ? <span>Schon im Plan.</span> : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
          {picked.length > 0 ? <p>{`${picked.length} ausgewählt`}</p> : null}
          <Button
            type="button"
            disabled={picked.length === 0 || !memberGroup}
            onClick={() => {
              if (!memberGroup) {
                return;
              }
              addMembers(
                memberGroup.id,
                hits.filter((person) => picked.includes(person.id)),
              );
              closeMembers(false);
            }}
          >
            Hinzufügen
          </Button>
          <h3 className="font-medium">Bereits in dieser Gruppe</h3>
          {memberRows.length === 0 ? <p>Keine Mitglieder.</p> : null}
          <ul className="grid gap-2">
            {memberRows.map((row, index) => (
              <li key={row.key} className="flex flex-wrap items-center gap-2">
                <span>{lastNameOf(row)}</span>
                <Button
                  type="button"
                  variant="outline"
                  disabled={index === 0}
                  aria-label={`${lastNameOf(row)} nach oben`}
                  onClick={() => moveRow(row.key, -1)}
                >
                  Nach oben
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={index === memberRows.length - 1}
                  aria-label={`${lastNameOf(row)} nach unten`}
                  onClick={() => moveRow(row.key, 1)}
                >
                  Nach unten
                </Button>
                <Button type="button" variant="outline" onClick={() => assignGroup(row.key, null)}>
                  Aus Gruppe entfernen
                </Button>
                <label className="inline-flex items-center gap-2 text-sm">
                  In Gruppe verschieben
                  <select
                    aria-label={`${lastNameOf(row)} in Gruppe verschieben`}
                    defaultValue=""
                    onChange={(event) => {
                      const next = event.target.value;
                      if (next) {
                        assignGroup(row.key, next);
                      }
                      event.target.value = '';
                    }}
                  >
                    <option value="">Gruppe wählen</option>
                    {orderedGroups
                      .filter((group) => group.id !== memberGroup?.id)
                      .map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                  </select>
                </label>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => closeMembers(false)}>
              Abbrechen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DndContext>
  );
}

function GroupHeading({
  section,
  groups,
  writable,
  onMove,
  onRename,
  onMembers,
  onDelete,
}: {
  section: Section;
  groups: DraftGroup[];
  writable: boolean;
  onMove: (id: string, direction: -1 | 1) => void;
  onRename: (group: DraftGroup) => void;
  onMembers: (groupId: string) => void;
  onDelete: (group: DraftGroup, memberCount: number) => void;
}) {
  if (section.kind === 'none') {
    return <h2 className="bg-muted px-2 py-2 text-left font-medium">Ohne Gruppe</h2>;
  }
  const index = groups.findIndex((group) => group.id === section.group.id);
  return (
    <div className="flex flex-wrap items-center gap-2 bg-muted px-2 py-2 text-left">
      <h2 className="font-medium">
        <span className="text-muted-foreground">{`${index + 1}. `}</span>
        {section.group.name}
      </h2>
      {writable ? (
        <span className="inline-flex shrink-0 items-center gap-0.5">
          <MobileIconButton
            label={`${section.group.name} nach oben`}
            disabled={index === 0}
            onClick={() => onMove(section.group.id, -1)}
          >
            <ChevronUpIcon className="size-4" aria-hidden />
          </MobileIconButton>
          <MobileIconButton
            label={`${section.group.name} nach unten`}
            disabled={index === groups.length - 1}
            onClick={() => onMove(section.group.id, 1)}
          >
            <ChevronDownIcon className="size-4" aria-hidden />
          </MobileIconButton>
          <MobileIconButton
            label="Mitglieder hinzufügen"
            onClick={() => onMembers(section.group.id)}
          >
            <UserPlusIcon className="size-4" aria-hidden />
          </MobileIconButton>
          <MobileIconButton label="Umbenennen" onClick={() => onRename(section.group)}>
            <PencilIcon className="size-4" aria-hidden />
          </MobileIconButton>
          <MobileIconButton
            label="Gruppe löschen"
            onClick={() => onDelete(section.group, section.rows.length)}
          >
            <Trash2Icon className="size-4" aria-hidden />
          </MobileIconButton>
        </span>
      ) : null}
    </div>
  );
}

function MemberCard({
  row,
  index,
  total,
  focusedDay,
  writable,
  meId,
  selectedIds,
  recent,
  patchRow,
  removeRow,
  moveRow,
}: {
  row: DraftRow;
  index: number;
  total: number;
  focusedDay: WeeklyPlanDay;
  writable: boolean;
  meId: string | undefined;
  selectedIds: readonly string[];
  recent: (row: DraftRow, day: WeeklyPlanDay) => boolean;
  patchRow: (key: string, recipe: (row: DraftRow) => DraftRow) => void;
  removeRow: (key: string) => void;
  moveRow: (key: string, direction: -1 | 1) => void;
}) {
  const chrome = personChrome(row, meId, selectedIds);
  const personName = row.firstName ? `${lastNameOf(row)}, ${row.firstName}` : lastNameOf(row);
  const nameLine = `${index + 1}. ${personName}`;
  return (
    <article
      className={cn('rounded-md border p-3', LIST_ROW_CLASS[chrome])}
      aria-label={rowTitle(row)}
    >
      <p className="line-clamp-2 font-medium" title={nameLine}>
        {nameLine}
      </p>
      {row.freeName ? <p className="text-xs text-muted-foreground">ohne Konto</p> : null}
      {writable ? (
        <div className="mt-2 flex flex-wrap gap-1">
          <MobileIconButton
            label={`${lastNameOf(row)} nach oben`}
            disabled={index === 0}
            onClick={() => moveRow(row.key, -1)}
          >
            <ChevronUpIcon className="size-4" aria-hidden />
          </MobileIconButton>
          <MobileIconButton
            label={`${lastNameOf(row)} nach unten`}
            disabled={index === total - 1}
            onClick={() => moveRow(row.key, 1)}
          >
            <ChevronDownIcon className="size-4" aria-hidden />
          </MobileIconButton>
          <MobileIconButton label={`${rowTitle(row)} entfernen`} onClick={() => removeRow(row.key)}>
            <Trash2Icon className="size-4" aria-hidden />
          </MobileIconButton>
        </div>
      ) : null}
      <p className="mt-2 text-sm">
        <span className="text-muted-foreground">Einsatz als </span>
        {assignmentField(row, writable, patchRow)}
      </p>
      <dd className="mt-2">
        {cellField(row, focusedDay, writable, recent(row, focusedDay), patchRow)}
      </dd>
    </article>
  );
}

function SectionRows({
  section,
  groups,
  span,
  columns,
  writable,
  meId,
  selectedIds,
  recent,
  patchRow,
  removeRow,
  moveRow,
  moveGroup,
  onRename,
  onMembers,
  onDelete,
}: {
  section: Section;
  groups: DraftGroup[];
  span: number;
  columns: WeeklyPlanDay[];
  writable: boolean;
  meId: string | undefined;
  selectedIds: readonly string[];
  recent: (row: DraftRow, day: WeeklyPlanDay) => boolean;
  patchRow: (key: string, recipe: (row: DraftRow) => DraftRow) => void;
  removeRow: (key: string) => void;
  moveRow: (key: string, direction: -1 | 1) => void;
  moveGroup: (id: string, direction: -1 | 1) => void;
  onRename: (group: DraftGroup) => void;
  onMembers: (groupId: string) => void;
  onDelete: (group: DraftGroup, memberCount: number) => void;
}) {
  return (
    <>
      <GroupTableRow
        section={section}
        groups={groups}
        span={span}
        writable={writable}
        onMove={moveGroup}
        onRename={onRename}
        onMembers={onMembers}
        onDelete={onDelete}
      />
      {section.kind === 'group' && section.rows.length === 0 ? (
        <TableRow>
          <TableCell colSpan={span}>Keine Mitglieder.</TableCell>
        </TableRow>
      ) : null}
      {section.rows.map((row, index) => (
        <MemberTableRow
          key={row.key}
          row={row}
          index={index}
          total={section.rows.length}
          columns={columns}
          writable={writable}
          meId={meId}
          selectedIds={selectedIds}
          recent={recent}
          patchRow={patchRow}
          removeRow={removeRow}
          moveRow={moveRow}
        />
      ))}
    </>
  );
}

function GroupTableRow({
  section,
  groups,
  span,
  writable,
  onMove,
  onRename,
  onMembers,
  onDelete,
}: {
  section: Section;
  groups: DraftGroup[];
  span: number;
  writable: boolean;
  onMove: (id: string, direction: -1 | 1) => void;
  onRename: (group: DraftGroup) => void;
  onMembers: (groupId: string) => void;
  onDelete: (group: DraftGroup, memberCount: number) => void;
}) {
  if (section.kind === 'none') {
    return (
      <TableRow className="bg-muted hover:bg-muted">
        <TableCell colSpan={span} className="bg-muted p-0">
          <DropHeading>
            <div className="sticky left-0 flex w-max flex-nowrap items-baseline gap-3 bg-muted px-2 py-2 text-left">
              <span role="rowheader" className="font-medium whitespace-nowrap">
                Ohne Gruppe
              </span>
              {writable ? (
                <span className="text-sm font-normal whitespace-nowrap text-muted-foreground">
                  Bestehende Zeilen ohne Überbegriff. In eine Gruppe verschieben.
                </span>
              ) : null}
            </div>
          </DropHeading>
        </TableCell>
      </TableRow>
    );
  }
  const index = groups.findIndex((group) => group.id === section.group.id);
  return (
    <SortableGroupRow id={`group:${section.group.id}`} span={span}>
      <div className="sticky left-0 flex w-max flex-nowrap items-center gap-2 bg-muted px-2 py-1 text-left font-medium">
        <span role="rowheader" className="whitespace-nowrap">
          <span className="text-muted-foreground">{`${index + 1}. `}</span>
          {section.group.name}
        </span>
        {writable ? (
          <span className="inline-flex shrink-0 items-center gap-0.5">
            <IconButton
              label={`${section.group.name} nach oben`}
              disabled={index === 0}
              onClick={() => onMove(section.group.id, -1)}
            >
              <ChevronUpIcon className="size-4" aria-hidden />
            </IconButton>
            <IconButton
              label={`${section.group.name} nach unten`}
              disabled={index === groups.length - 1}
              onClick={() => onMove(section.group.id, 1)}
            >
              <ChevronDownIcon className="size-4" aria-hidden />
            </IconButton>
            <IconButton label="Mitglieder hinzufügen" onClick={() => onMembers(section.group.id)}>
              <UserPlusIcon className="size-4" aria-hidden />
            </IconButton>
            <IconButton label="Umbenennen" onClick={() => onRename(section.group)}>
              <PencilIcon className="size-4" aria-hidden />
            </IconButton>
            <IconButton
              label="Gruppe löschen"
              onClick={() => onDelete(section.group, section.rows.length)}
            >
              <Trash2Icon className="size-4" aria-hidden />
            </IconButton>
          </span>
        ) : null}
      </div>
    </SortableGroupRow>
  );
}

function DropHeading({ children }: { children: ReactNode }) {
  const droppable = useDroppable({ id: 'bucket:none' });
  return <div ref={droppable.setNodeRef}>{children}</div>;
}

function SortableGroupRow({
  id,
  span,
  children,
}: {
  id: string;
  span: number;
  children: ReactNode;
}) {
  const sortable = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  };
  return (
    <TableRow ref={sortable.setNodeRef} style={style} className="bg-muted hover:bg-muted">
      <TableCell colSpan={span} className="bg-muted p-0 whitespace-nowrap">
        <div {...sortable.attributes} {...sortable.listeners}>
          {children}
        </div>
      </TableCell>
    </TableRow>
  );
}

function MemberTableRow({
  row,
  index,
  total,
  columns,
  writable,
  meId,
  selectedIds,
  recent,
  patchRow,
  removeRow,
  moveRow,
}: {
  row: DraftRow;
  index: number;
  total: number;
  columns: WeeklyPlanDay[];
  writable: boolean;
  meId: string | undefined;
  selectedIds: readonly string[];
  recent: (row: DraftRow, day: WeeklyPlanDay) => boolean;
  patchRow: (key: string, recipe: (row: DraftRow) => DraftRow) => void;
  removeRow: (key: string) => void;
  moveRow: (key: string, direction: -1 | 1) => void;
}) {
  const sortable = useSortable({ id: `row:${row.key}` });
  const chrome = personChrome(row, meId, selectedIds);
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  };
  return (
    <TableRow
      ref={sortable.setNodeRef}
      style={style}
      aria-label={row.displayName}
      className={chrome === 'selected' ? 'bg-muted' : undefined}
    >
      <TableCell
        className={cn('sticky z-10 p-1 whitespace-nowrap', stickyTone(chrome, true))}
        style={COL_STYLE.nr}
      >
        <div className="flex items-center gap-0.5">
          <span {...sortable.attributes} {...sortable.listeners}>
            {`${index + 1}.`}
          </span>
          {writable ? (
            <>
              <IconButton
                label={`${lastNameOf(row)} nach oben`}
                disabled={index === 0}
                onClick={() => moveRow(row.key, -1)}
              >
                <ChevronUpIcon className="size-4" aria-hidden />
              </IconButton>
              <IconButton
                label={`${lastNameOf(row)} nach unten`}
                disabled={index === total - 1}
                onClick={() => moveRow(row.key, 1)}
              >
                <ChevronDownIcon className="size-4" aria-hidden />
              </IconButton>
              <IconButton label={`${rowTitle(row)} entfernen`} onClick={() => removeRow(row.key)}>
                <Trash2Icon className="size-4" aria-hidden />
              </IconButton>
            </>
          ) : null}
        </div>
      </TableCell>
      <TableCell
        className={cn(
          'sticky z-10 min-w-0 break-words whitespace-normal',
          stickyTone(chrome, false),
        )}
        style={COL_STYLE.last}
      >
        <span>{lastNameOf(row)}</span>
        {row.freeName && writable ? (
          <span className="ml-2 text-xs text-muted-foreground">ohne Konto</span>
        ) : null}
      </TableCell>
      <TableCell
        className={cn('sticky z-10 min-w-0 whitespace-normal', stickyTone(chrome, false))}
        style={COL_STYLE.first}
      >
        {row.firstName ?? ''}
      </TableCell>
      <TableCell
        className={cn('sticky z-10 min-w-0 whitespace-normal', stickyTone(chrome, false))}
        style={COL_STYLE.role}
      >
        {assignmentField(row, writable, patchRow)}
      </TableCell>
      {columns.map((day) => (
        <TableCell
          key={day}
          className={cn(
            'min-w-32 whitespace-normal',
            chrome === 'selected' ? 'bg-muted' : undefined,
          )}
        >
          {cellField(row, day, writable, recent(row, day), patchRow)}
        </TableCell>
      ))}
    </TableRow>
  );
}

function stickyTone(chrome: ReturnType<typeof personChrome>, edge: boolean): string {
  if (chrome === 'selected') {
    return 'bg-muted';
  }
  return edge && chrome === 'own' ? 'border-l-2 border-l-primary bg-background' : 'bg-background';
}

function MobileIconButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-11 shrink-0"
      disabled={disabled}
      aria-label={label}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function IconButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-8 shrink-0"
      disabled={disabled}
      aria-label={label}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}
